import type { PhaseEightDto, PhaseQuestionsDto, PhaseScrapDto, RoleDto, WaveECommonDto } from '@bk/contracts';
import { useState, type CSSProperties, type ReactNode } from 'react';
import { api } from '../../lib/api';
import { useTenant } from '../../lib/useTenant';
import { Figures, Question } from './Fase';
import { PhaseFrame, usePhase } from './fasi-comune';
import type { PhasePageProps } from './fasi-pagine';

/**
 * La fase 8 «L'organizzazione: ruoli, mansioni e competenze» (docs/mockup/percorso-sinapsi/11-fase8; contratto n. 1
 * versione 2.2, art. 8, fase 8; ADR 0178): l'organigramma per luogo, i ruoli con capo, contratto e dimensionamento, la
 * scheda di ogni ruolo, i referenti di sede, lo scarto, le cose che aiuterebbero, le domande sui ruoli scoperti o
 * frazionari. Nessun nome di persona: l'organigramma è di ruoli.
 */

const chip = (tone: 'ok' | 'gold' | 'cy' | 'mute'): CSSProperties => ({
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 999,
  font: "500 11px 'JetBrains Mono', monospace",
  letterSpacing: '.06em',
  textTransform: 'uppercase',
  border: `1px solid ${tone === 'ok' ? 'rgba(70,224,176,.5)' : tone === 'gold' ? 'rgba(255,214,140,.5)' : tone === 'cy' ? 'rgba(60,240,255,.5)' : 'rgba(234,240,255,.2)'}`,
  color: tone === 'ok' ? '#46e0b0' : tone === 'gold' ? '#ffd68c' : tone === 'cy' ? '#3cf0ff' : '#8b97b5',
});
export const Chip = ({ tone, children }: { tone: 'ok' | 'gold' | 'cy' | 'mute'; children: ReactNode }) => <span style={chip(tone)}>{children}</span>;
export const GradeChip = ({ g }: { g: string }) => <Chip tone={g === 'certo' ? 'ok' : 'gold'}>{g}</Chip>;
const node: CSSProperties = { border: '1px solid rgba(234,240,255,.16)', borderRadius: 12, padding: '10px 14px', fontSize: 13, color: '#c3cbe0', display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 };
const row: CSSProperties = { display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))' };

/** Lo scarto della fase nella forma delle pagine di prova: voce, l'azienda di oggi, da dove lo so. */
export function WaveScrap({ list, intro, missing }: { list: PhaseScrapDto[]; intro: string; missing: string }) {
  if (!list.length) return null;
  const label = (o: PhaseScrapDto['oggi']) => (o === 'coperta' ? ['cy', 'Coperto da altri'] : o === 'manca' ? ['gold', missing] : o === 'ha' ? ['ok', 'C’è'] : ['ok', 'Nessuno scarto']) as ['ok' | 'gold' | 'cy', string];
  return (
    <section className="sn-panel gold" id="scarto" style={{ gap: 10, padding: '20px 24px' }}>
      <h2 className="sn-h2">Lo scarto di questa fase</h2>
      <p className="sn-note" style={{ fontSize: 14 }}>
        {intro}
      </p>
      <table className="sn-tbl wide">
        <thead>
          <tr>
            <th>Voce</th>
            <th style={{ width: 210 }}>L’azienda di oggi</th>
            <th style={{ width: 200 }}>Da dove lo so</th>
          </tr>
        </thead>
        <tbody>
          {list.map((x, k) => {
            const [tone, text] = label(x.oggi);
            return (
              <tr key={k}>
                <td>
                  <b>{x.voce}</b>
                  {x.oggi !== 'manca' && x.oggi !== 'ha' && x.testo ? `: ${x.testo.split('. ')[0]}` : ''}
                </td>
                <td>
                  <Chip tone={tone}>{text}</Chip>
                  {x.oggi !== 'nessuno' && x.testo.includes('. ') && <span className="sub">{x.testo.split('. ').slice(1).join('. ')}</span>}
                  {x.oggi === 'coperta' && !x.testo.includes('. ') && x.testo && <span className="sub">{x.testo}</span>}
                </td>
                <td className="f on">{x.perche ?? '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="sn-note">Lo scarto dice cosa le fonti mostrano o non mostrano oggi, non se fate bene o male: la diagnosi (fase 10) lo raccoglie per reparto, le proposte (fase 11) lo prezzano.</p>
    </section>
  );
}

/** «Cose che aiuterebbero qui»: voci del registro di fasi già fatte, non domande nuove (art. 7-bis.13). */
export function Helpful({ d, onChange }: { d: WaveECommonDto; onChange: () => void }) {
  const { t, isManager } = useTenant();
  const [open, setOpen] = useState<string | null>(null);
  if (!d.aiuterebbero.length) return null;
  return (
    <section className="sn-panel" style={{ gap: 10, padding: '20px 24px' }}>
      <h2 className="sn-h2">Cose che aiuterebbero qui</h2>
      <p className="sn-note" style={{ fontSize: 14 }}>
        Non sono domande nuove: sono voci del registro di fasi già fatte. Se rispondi, questa fase si aggiorna da sola, senza rifarla.
      </p>
      <table className="sn-tbl wide">
        <tbody>
          {d.aiuterebbero.map((q) => (
            <tr key={q.id}>
              <td>
                <b>{q.voce}</b>
                {open === q.id && (
                  <div style={{ marginTop: 10 }}>
                    <Question
                      q={q}
                      canAnswer={isManager}
                      send={(body) =>
                        api<PhaseQuestionsDto>(t(`/onboarding/domande/${q.id}/risposta`), { method: 'POST', body }).then(() => {
                          setOpen(null);
                          onChange();
                        })
                      }
                    />
                  </div>
                )}
              </td>
              <td style={{ width: 70, fontFamily: 'var(--sn-mono)', fontSize: 13 }}>fase {q.fase}</td>
              <td style={{ width: 150 }}>
                <Chip tone="gold">{q.stato === 'aperta' ? 'Aperta' : 'Scelta di Timonio'}</Chip>
              </td>
              <td className="sn-note">{q.cambia}</td>
              <td style={{ width: 90 }}>
                <button type="button" className="sn-link" onClick={() => setOpen(open === q.id ? null : q.id)}>
                  {open === q.id ? 'Chiudi' : 'Rispondi'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

/** Una riga chiave-valore della scheda. */
export const Kv = ({ k, children }: { k: string; children: ReactNode }) => (
  <tr>
    <td style={{ width: 190, color: '#8b97b5', fontSize: 13 }}>{k}</td>
    <td>{children}</td>
  </tr>
);
const Src = ({ s }: { s: string | null }) => (s ? <span className="sn-src"> · {s}</span> : null);

/** La scheda del ruolo (art. 3.116): nomi, capo, responsabilità, incarichi, mansioni e compiti, competenze, contratto. */
function RoleCard({ r }: { r: RoleDto }) {
  return (
    <article className="sn-panel lit" id="scheda" style={{ gap: 12, padding: '20px 24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <h2 className="sn-h2">Scheda del ruolo · {r.nome_uso}</h2>
        <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <Chip tone="ok">{r.interno ? 'Interno' : 'Esterno'}</Chip>
          {r.persone && <Chip tone="mute">{r.persone}</Chip>}
          <GradeChip g={r.grado} />
          {r.secondo_il_consulente && <Chip tone="gold">Secondo il consulente</Chip>}
        </span>
      </div>
      <table className="sn-tbl wide">
        <tbody>
          <Kv k="Nome d’uso">{r.nome_uso}</Kv>
          <Kv k="Nome ufficiale">
            {r.nome_ufficiale}
            {r.codice_professione && <span className="sn-src"> · CP2021 {r.codice_professione}</span>}
          </Kv>
          <Kv k="Capo e chi gli risponde">
            {r.capo ?? 'nessuno: è il vertice'} · {r.gli_rispondono.length ? `gli rispondono: ${r.gli_rispondono.join(', ').toLowerCase()}` : 'nessuno gli risponde'}
          </Kv>
          <Kv k="Responsabilità">
            {r.responsabilita.map((x, k) => (
              <span key={k} style={{ display: 'block' }}>
                {k + 1}. {x.testo}
                <Src s={x.fonte} />
              </span>
            ))}
          </Kv>
          {r.incarichi.length > 0 && (
            <Kv k="Incarichi di legge">
              {r.incarichi.map((x, k) => (
                <span key={k} style={{ display: 'block' }}>
                  {x.testo} · {x.fonte} <Chip tone="cy">Lo chiede la legge</Chip>
                </span>
              ))}
            </Kv>
          )}
          {r.mansioni.length > 0 && (
            <Kv k="Mansioni e compiti">
              <table className="sn-tbl">
                <thead>
                  <tr>
                    <th>Mansione</th>
                    <th>Compiti · importanza · frequenza</th>
                  </tr>
                </thead>
                <tbody>
                  {r.mansioni.map((m, k) => (
                    <tr key={k}>
                      <td>
                        <b>{m.nome}</b>
                        <Src s={m.fonte} />
                      </td>
                      <td>
                        {m.compiti.map((c, j) => (
                          <span key={j} className={j ? 'sub' : undefined} style={{ display: 'block' }}>
                            {c.testo} · {c.importanza} · {c.frequenza}
                          </span>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Kv>
          )}
          {r.competenze.length > 0 && <Kv k="Competenze">{r.competenze.join('; ')}</Kv>}
          <Kv k="Esperienza">
            {r.esperienza.length
              ? r.esperienza.map((x, k) => (
                  <span key={k}>
                    {k ? ' · ' : ''}
                    {x.testo}
                    <Src s={x.fonte} />
                  </span>
                ))
              : 'le fonti non la dicono'}
          </Kv>
          {r.crescita.length > 0 && <Kv k="Crescita">{r.crescita.join(' · ')}</Kv>}
          <Kv k="Contratto e costo">
            {r.contratto_esteso}
            <Src s={r.contratto_fonte} />
            {r.contratto_nota && <span className="sub">{r.contratto_nota}</span>}
          </Kv>
          {r.corsi.length > 0 && <Kv k="Corsi di sicurezza obbligatori">{r.corsi.join(' · ')}</Kv>}
          {r.indicatori.length > 0 && <Kv k="Indicatori">{r.indicatori.join(' · ')}</Kv>}
          {[...r.processi, ...r.impianti, ...r.programmi, ...r.procedure].length > 0 && (
            <Kv k="Processi, impianti, programmi, procedure">{[r.processi, r.impianti, r.programmi, r.procedure].map((x) => x.join(', ')).filter(Boolean).join(' · ')}</Kv>
          )}
          {r.chi_lo_fa && <Kv k="Chi lo fa oggi">{r.chi_lo_fa}</Kv>}
        </tbody>
      </table>
    </article>
  );
}

export function Fase8Page({ next, onNext, sealing, stopped = false }: PhasePageProps) {
  const { q, reload, confirm, redo } = usePhase<PhaseEightDto>(8);
  const [card, setCard] = useState<string | null>(null);
  const [functions, setFunctions] = useState(false);
  return (
    <PhaseFrame<PhaseEightDto & { scarto_pagina: PhaseEightDto['scarto'] }>
      // Lo scarto si mostra nella forma della pagina di prova, prima delle domande.
      d={q.data && { ...q.data, scarto: [], scarto_pagina: q.data.scarto }}
      error={q.error}
      eyebrow="Fase 8 · Organizzazione e ruoli"
      title={(d) => `Chi fa cosa in ${d.azienda ?? 'la tua azienda'}, con tutti i responsabili`}
      working="Preparo l’organizzazione"
      questionsIntro="Solo sui ruoli che il dimensionamento dà scoperti o frazionari."
      questionsEmpty="Nessuna domanda: il dimensionamento non lascia ruoli scoperti o frazionari."
      scrapIntro=""
      next={next}
      onNext={onNext}
      sealing={sealing}
      stopped={stopped}
      onConfirm={confirm}
      onRedo={redo}
      reload={reload}
      beforeQuestions={(d) => (
        <>
          <WaveScrap list={d.scarto_pagina} intro="Il dimensionamento per ruolo e per sede accanto agli addetti veri, con lo scarto scritto, e i ruoli scoperti o coperti da chi fa altro." missing="Scoperto" />
          <Helpful d={d} onChange={reload} />
        </>
      )}
    >
      {(d) => {
        const top = d.organigramma.filter((n) => !n.padre);
        const second = d.organigramma.filter((n) => n.padre && top.some((t) => t.id === n.padre));
        const third = d.organigramma.filter((n) => n.padre && second.some((s) => s.id === n.padre) && !n.esterni);
        const shown = d.ruoli.find((r) => r.id === card) ?? [...d.ruoli].filter((r) => r.interno).sort((a, b) => b.mansioni.length - a.mansioni.length || (b.persone ? 1 : 0) - (a.persone ? 1 : 0))[0];
        return (
          <>
            <Figures list={d.numeri} />
            <section className="sn-panel" style={{ gap: 12, padding: '20px 24px' }}>
              <h2 className="sn-h2">L’organigramma, per luogo</h2>
              {top.map((n) => (
                <div key={n.id} style={{ ...node, borderColor: 'rgba(60,240,255,.45)' }}>
                  <b style={{ color: '#eaf0ff' }}>{n.nome}</b>
                  {n.ruoli}
                </div>
              ))}
              {second.length > 0 && (
                <div style={row}>
                  {second.map((n) => (
                    <div key={n.id} style={{ ...node, borderStyle: n.esterni ? 'dashed' : 'solid' }}>
                      <b style={{ color: '#eaf0ff' }}>{n.nome}</b>
                      {n.ruoli}
                    </div>
                  ))}
                </div>
              )}
              {third.length > 0 && (
                <div style={row}>
                  {third.map((n) => (
                    <div key={n.id} style={node}>
                      <b style={{ color: '#eaf0ff' }}>{n.nome}</b>
                      {n.ruoli}
                    </div>
                  ))}
                </div>
              )}
              {d.funzionigramma.length > 0 && (
                <div>
                  <button type="button" className="sn-link" onClick={() => setFunctions(!functions)}>
                    {functions ? 'Chiudi il funzionigramma' : 'Vedi il funzionigramma: le funzioni e i ruoli che le svolgono'}
                  </button>
                  {functions && (
                    <table className="sn-tbl wide" style={{ marginTop: 8 }}>
                      <tbody>
                        {d.funzionigramma.map((f) => (
                          <Kv key={f.funzione} k={f.funzione}>
                            {f.ruoli.join(', ').toLowerCase()}
                          </Kv>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </section>
            <section className="sn-panel flush" style={{ gap: 0 }}>
              <div style={{ padding: '20px 24px 8px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                <h2 className="sn-h2">I ruoli</h2>
                <p className="sn-note" style={{ fontSize: 14 }}>
                  Nome d’uso e, sotto, il nome ufficiale della professione. Livelli e costi dal contratto collettivo applicato (fase 1) e dalla tabella ministeriale del costo del lavoro; compensi degli esterni dai numeri del settore della libreria, o «da confermare» quando mancano. I ruoli di organi e soci non hanno contratto collettivo: lo dice l’atto.
                </p>
              </div>
              <table className="sn-tbl wide">
                <thead>
                  <tr>
                    <th>Ruolo</th>
                    <th>Capo</th>
                    <th style={{ width: 90 }}>Interno o esterno</th>
                    <th>Contratto, livello, costo annuo</th>
                    <th>Servirebbero · ci sono</th>
                    <th style={{ width: 110 }}>Grado</th>
                  </tr>
                </thead>
                <tbody>
                  {d.ruoli.map((r) => (
                    <tr key={r.id} onClick={() => setCard(r.id)} style={{ cursor: 'pointer', background: shown?.id === r.id ? 'rgba(60,240,255,.05)' : undefined }}>
                      <td>
                        <b>{r.nome_uso}</b>
                        {r.interno && <span className="sub">{r.nome_ufficiale}</span>}
                      </td>
                      <td>{r.capo ?? '—'}</td>
                      <td>{r.interno ? 'interno' : 'esterno'}</td>
                      <td>
                        {r.contratto}
                        {r.contratto_fonte && <span className="sn-src"> · {r.contratto_fonte}</span>}
                        {r.contratto_nota && <span className="sub">{r.contratto_nota}</span>}
                      </td>
                      <td>{r.dimensionamento}</td>
                      <td>
                        <GradeChip g={r.grado} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {d.nota && (
                <p className="sn-note" style={{ padding: '12px 24px 18px' }}>
                  {d.nota}
                </p>
              )}
            </section>
            {shown && <RoleCard r={shown} />}
            {d.referenti.length > 0 && (
              <section className="sn-panel" style={{ gap: 10, padding: '20px 24px' }}>
                <h2 className="sn-h2">I referenti di sede</h2>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {d.referenti.map((x) => (
                    <Chip key={x.sede} tone="ok">
                      {x.sede} · {x.ruolo}
                    </Chip>
                  ))}
                </div>
              </section>
            )}
          </>
        );
      }}
    </PhaseFrame>
  );
}
